import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { voiceApi } from '../../api/voice'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { percent, rateWords, REGISTER_AXES, scenarioLabel } from '../../lib/voice'
import type { ScenarioView } from '../../types/voice'
import { RegisterRadar } from './RegisterRadar'

// The structured descriptor: measured facts read-only, the prose summary and never-say list
// editable by the person (they are the only authority on their own voice). Provisional means no
// model has written the prose yet; the numbers are still real.
export function DescriptorCard({ scenario, overall }: { scenario: ScenarioView; overall?: ScenarioView }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const d = scenario.descriptor
  const [editing, setEditing] = useState(false)
  const [summary, setSummary] = useState(d.summary ?? '')
  const [neverSay, setNeverSay] = useState(d.neverSay.join('\n'))

  useEffect(() => {
    setSummary(d.summary ?? '')
    setNeverSay(d.neverSay.join('\n'))
    setEditing(false)
  }, [d.summary, d.neverSay, scenario.scenario])

  const save = useMutation({
    mutationFn: () =>
      voiceApi.editDescriptor(
        spoke,
        scenario.scenario,
        summary,
        neverSay.split('\n').map((s) => s.trim()).filter(Boolean),
      ),
    onSuccess: () => {
      setEditing(false)
      void qc.invalidateQueries({ queryKey: ['voice-profile', spoke] })
    },
  })

  return (
    <div className="descriptor">
      <div className="descriptor-radar">
        <RegisterRadar register={d.register} compare={overall && overall.scenario !== scenario.scenario ? overall.descriptor.register : undefined} />
        <ul className="axis-legend">
          {REGISTER_AXES.map((a) => (
            <li key={a.key}>
              <span>{a.label}</span>
              <span className="muted">
                {a.low} ⟷ {a.high}
              </span>
              <strong>{percent(d.register[a.key])}</strong>
            </li>
          ))}
        </ul>
      </div>

      <div className="descriptor-facts">
        <h3>
          {scenario.scenario === 'all' ? 'How you write, overall' : `How you write ${scenarioLabel(scenario.scenario).replace(/^Email · /, '').toLowerCase()}`}
          {d.provisional && <span className="tag">measured only</span>}
        </h3>
        {editing ? (
          <>
            <label>
              In words
              <textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Describe your own voice, or leave it to the model." />
            </label>
            <label>
              Never say (one per line)
              <textarea rows={4} value={neverSay} onChange={(e) => setNeverSay(e.target.value)} />
            </label>
            <div className="actions">
              <button className="button primary" onClick={() => save.mutate()} disabled={save.isPending}>
                Save
              </button>
              <button className="button" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
            {save.isError && <p className="error">{save.error instanceof RefusedError ? save.error.refusal.message : save.error.message}</p>}
          </>
        ) : (
          <>
            {d.summary ? <p className="summary">{d.summary}</p> : <p className="muted">No prose yet. The numbers below were measured from your mail; connect a model and rebuild to have it described in words, or write it yourself.</p>}
            {d.neverSay.length > 0 && (
              <p className="never-line">
                <strong>Never:</strong> {d.neverSay.map((n) => <span key={n} className="chip never">{n}</span>)}
              </p>
            )}
            {(d.preferences?.length ?? 0) > 0 && (
              <div className="taught">
                <strong>What you taught it</strong>
                <ul>{d.preferences!.map((n) => <li key={n}>{n}</li>)}</ul>
              </div>
            )}
            <button className="button small" onClick={() => setEditing(true)}>
              Edit the words
            </button>
          </>
        )}

        <dl className="facts compact">
          <dt>Opens with</dt>
          <dd>{d.greetings.slice(0, 3).map((g) => `“${g.text}” ${percent(g.rate)}`).join(' · ') || '—'}</dd>
          <dt>Closes with</dt>
          <dd>{d.closings.slice(0, 3).map((c) => `“${c.text}” ${percent(c.rate)}`).join(' · ') || '—'}</dd>
          <dt>Length</dt>
          <dd>
            about {Math.round(d.length.medianWords)} words in {Math.round(d.length.medianSentences)} sentences; replies about {Math.round(d.length.medianWordsReplies)} words; {d.length.meanSentenceLength.toFixed(0)} words a sentence
          </dd>
          <dt>Contractions</dt>
          <dd>{rateWords(d.contractionRate, 'almost never', 'sometimes', 'freely')}</dd>
          <dt>Punctuation</dt>
          <dd>
            exclamation marks {rateWords(d.punctuation.exclamationRate, 'never', 'rarely', 'often')} · dashes {rateWords(d.punctuation.dashRate, 'never', 'occasionally', 'often')} · questions{' '}
            {rateWords(d.punctuation.questionRate, 'rarely', 'sometimes', 'often')} · ellipses {rateWords(d.punctuation.ellipsisRate, 'never', 'rarely', 'often')} · semicolons{' '}
            {rateWords(d.punctuation.semicolonRate, 'never', 'rarely', 'sometimes')}
          </dd>
          {d.signaturePhrases.length > 0 && (
            <>
              <dt>Your phrases</dt>
              <dd>{d.signaturePhrases.map((p) => `“${p}”`).join(' · ')}</dd>
            </>
          )}
          <dt>Measured from</dt>
          <dd>
            {scenario.messageCount} messages
            {scenario.selfCheckScore !== null && ` · self-check ${scenario.selfCheckScore.toFixed(2)} on a message you sent`}
            {scenario.modelId && ` · described by ${scenario.modelId}`}
          </dd>
        </dl>
      </div>
    </div>
  )
}
